import { z } from 'zod';
import type { DecisaoParticipacao, FinalidadeEstado, Permissao } from '../enums/dominios';
import type { PessoaResumoDto } from './licitacao';

// Contratos do quadro entre front e API. Só uuids trafegam; nunca ids internos.

export type EstadoDto = {
  uuid: string;
  nome: string;
  finalidade: FinalidadeEstado;
  ordem: number;
  cor: string;
  icone: string;
  exigeMotivo: boolean;
  /** Motivos da lista do estado (ex.: Arquivo), na ordem de exibição. */
  motivos: MotivoDto[];
};

export type MotivoDto = { uuid: string; descricao: string };

export type GrupoDto = {
  uuid: string;
  estadoUuid: string;
  x: number;
  y: number;
  largura: number;
  altura: number;
  rotacao: number;
  versao: number;
};

export type ProcessoCartaoDto = {
  uuid: string;
  estadoUuid: string;
  numero: string;
  objeto: string;
  orgao: string | null;
  responsavel: { uuid: string; nome: string } | null;
  decisaoParticipacao: DecisaoParticipacao;
  x: number;
  y: number;
  versao: number;
};

/** Processo na Lixeira (fora do quadro), com o instante em que entrou nela (ISO 8601). */
export type ProcessoLixeiraDto = { uuid: string; numero: string; objeto: string; versao: number; entrouEm: string };

/** `veCartoes`: a pessoa vê os cartões do quadro (post-it ligado a um cartão só é compartilhado com ela se vê, RQ-NOT-008). */
export type MembroQuadroDto = PessoaResumoDto & { veCartoes: boolean };

export type QuadroDto = {
  quadro: { uuid: string; nome: string };
  estados: EstadoDto[];
  grupos: GrupoDto[];
  /** Processos visíveis no quadro: Novo, fluxo e Sem estado. Arquivo e Lixeira ficam fora. */
  processos: ProcessoCartaoDto[];
  lixeira: ProcessoLixeiraDto[];
  /** Pessoas com perfil no quadro (responsáveis, compartilhamento de notas). */
  membros: MembroQuadroDto[];
  /** Permissões de quem está vendo, para a tela mostrar só o que a pessoa pode fazer. */
  permissoes: Permissao[];
};

/** Aviso de uma mudança de estado (RQ-EST-006). A mudança só é gravada se a pessoa confirmar, com motivo. */
export type AvisoMudancaEstado = { codigo: string; mensagem: string };

/** Corpo da resposta 422 de uma mudança de estado com avisos não confirmados. */
export type RespostaAvisos = { mensagem: string; avisos: AvisoMudancaEstado[] };

const coordenada = z.number().finite().min(-100000).max(100000);

/** Nova geometria de um grupo (mover, redimensionar, inclinar; ou crescer e empurrar vizinhos). */
export const esquemaGeometriaGrupo = z.object({
  uuid: z.string().min(1),
  x: coordenada,
  y: coordenada,
  largura: z.number().finite(),
  altura: z.number().finite(),
  rotacao: z.number().finite(),
  versao: z.number().int().positive(),
});

/** Grupos que crescem/são empurrados como consequência da ação sobre o cartão: gravam na mesma transação (RQ-EST-005). */
const gruposAfetados = z.array(esquemaGeometriaGrupo).max(50).optional();

export const esquemaMoverProcesso = z.object({
  x: coordenada,
  y: coordenada,
  versao: z.number().int().positive(),
  grupos: gruposAfetados,
});

export const esquemaAlterarGrupos = z.object({
  grupos: z.array(esquemaGeometriaGrupo).min(1).max(50),
});

const versao = z.number().int().positive();

/** Posição relativa ao grupo do estado de destino; absoluta no quadro quando o destino não tem grupo. */
export const esquemaMudarEstado = z.object({
  estadoUuid: z.string().min(1),
  x: coordenada,
  y: coordenada,
  versao,
  motivoUuid: z.string().min(1).optional(),
  motivoTexto: z.string().trim().max(500).optional(),
  confirmarAvisos: z.boolean().optional(),
  grupos: gruposAfetados,
});

export const esquemaRestaurarProcesso = z.object({ versao });

/** Uuids dos processos da Lixeira a excluir: um (botão do item) ou todos (Esvaziar Lixeira). */
export const esquemaEsvaziarLixeira = z.object({ processos: z.array(z.string().min(1)).min(1).max(1000) });

export type MoverProcessoEntrada = z.infer<typeof esquemaMoverProcesso>;
export type AlterarGruposEntrada = z.infer<typeof esquemaAlterarGrupos>;
export type GeometriaGrupoEntrada = z.infer<typeof esquemaGeometriaGrupo>;
export type MudarEstadoEntrada = z.infer<typeof esquemaMudarEstado>;
export type RestaurarProcessoEntrada = z.infer<typeof esquemaRestaurarProcesso>;
export type EsvaziarLixeiraEntrada = z.infer<typeof esquemaEsvaziarLixeira>;
